import styled, { keyframes } from 'styled-components';
import { palette } from '../../theme/colors';

export const OVERLAY_FADE_IN_MS = 200;
export const CLOSE_FADE_OUT_MS = 200;
const MESSAGE_DELAY_MS = OVERLAY_FADE_IN_MS / 1.5;
const MESSAGE_LEAD_FADE_IN_MS = 400;
const MESSAGE_DETAIL_FADE_IN_MS = 700;
const MESSAGE_STAGGER_MS = 50;
const MESSAGE_REDUCED_MS = 120;
const OVERLAY_REDUCED_MS = 80;

/**
 * Minimum time the overlay stays visible so the enter animation runs in full
 * on fast switches.
 */
export const MIN_VISIBLE_MS = Math.max(
  OVERLAY_FADE_IN_MS,
  MESSAGE_DELAY_MS +
    Math.max(MESSAGE_LEAD_FADE_IN_MS, MESSAGE_STAGGER_MS + MESSAGE_DETAIL_FADE_IN_MS)
);

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const riseIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const SOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: ${palette.navyBlue};
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 24px;
  z-index: 1100;
  opacity: 0;

  &[data-phase='visible'] {
    animation: ${fadeIn} ${OVERLAY_FADE_IN_MS}ms ease-out forwards;
  }
  &[data-phase='leaving'] {
    animation: ${fadeOut} ${CLOSE_FADE_OUT_MS}ms ease-out forwards;
  }

  @media (prefers-reduced-motion: reduce) {
    &[data-phase='visible'] {
      animation: ${fadeIn} ${OVERLAY_REDUCED_MS}ms ease-out forwards;
    }

    &[data-phase='leaving'] {
      animation: ${fadeOut} ${OVERLAY_REDUCED_MS}ms ease-out forwards;
    }
  }
`;

export const SContent = styled.div`
  max-width: 320px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 12px 28px;
  transform-origin: center;

  [data-phase='visible'] & {
    animation: ${riseIn} ${OVERLAY_FADE_IN_MS}ms ease-out forwards;
  }

  [data-phase='leaving'] & {
    animation: ${fadeOut} ${CLOSE_FADE_OUT_MS}ms ease-out forwards;
  }

  @media (prefers-reduced-motion: reduce) {
    [data-phase='visible'] & {
      animation: ${fadeIn} ${OVERLAY_REDUCED_MS}ms ease-out forwards;
      transform: none;
    }

    [data-phase='leaving'] & {
      animation: ${fadeOut} ${OVERLAY_REDUCED_MS}ms ease-out forwards;
      transform: none;
    }
  }
`;

/** Fixed bounds keep the ring centered and prevent layout shifts. */
export const SSpinnerBounds = styled.div`
  position: relative;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
`;

export const SRing = styled.div`
  position: absolute;
  width: 64px;
  height: 64px;
  border-width: 3px;
  border-style: solid;
  border-radius: 32px;
  border-top-color: ${palette.earthYellow};
  border-right-color: ${palette.loaderDark};
  border-bottom-color: ${palette.loaderDark};
  border-left-color: ${palette.loaderDark};
  animation: ${spin} 1200ms cubic-bezier(0.5, 0, 0.5, 1) infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    border-top-color: ${palette.earthYellow};
    border-right-color: ${palette.earthYellow};
    border-bottom-color: ${palette.earthYellow};
    border-left-color: ${palette.earthYellow};
  }
`;

export const SLeadMessage = styled.div`
  align-self: stretch;
  font-family: 'Roboto-Medium', Roboto, sans-serif;
  font-size: 24px;
  line-height: 28px;
  font-weight: 600;
  color: ${palette.white};
  text-align: center;
  opacity: 0;

  [data-phase='visible'] & {
    animation: ${fadeIn} ${MESSAGE_LEAD_FADE_IN_MS}ms ease-out ${MESSAGE_DELAY_MS}ms forwards;
  }

  [data-phase='leaving'] & {
    opacity: 1;
    animation: ${fadeOut} ${CLOSE_FADE_OUT_MS}ms ease-out forwards;
  }

  @media (prefers-reduced-motion: reduce) {
    [data-phase='visible'] & {
      animation: ${fadeIn} ${MESSAGE_REDUCED_MS}ms ease-out 0ms forwards;
    }

    [data-phase='leaving'] & {
      opacity: 1;
      animation: ${fadeOut} ${MESSAGE_REDUCED_MS}ms ease-out 0ms forwards;
    }
  }
`;

export const SDetailMessage = styled.div`
  align-self: stretch;
  margin-top: 8px;
  font-family: 'Roboto-Medium', Roboto, sans-serif;
  font-size: 15px;
  line-height: 20px;
  font-weight: 500;
  color: ${palette.white};
  text-align: center;
  opacity: 0;

  [data-phase='visible'] & {
    animation: ${fadeIn} ${MESSAGE_DETAIL_FADE_IN_MS}ms ease-out
      ${MESSAGE_DELAY_MS + MESSAGE_STAGGER_MS}ms forwards;
  }

  [data-phase='leaving'] & {
    opacity: 1;
    animation: ${fadeOut} ${CLOSE_FADE_OUT_MS}ms ease-out forwards;
  }

  @media (prefers-reduced-motion: reduce) {
    [data-phase='visible'] & {
      animation: ${fadeIn} ${MESSAGE_REDUCED_MS}ms ease-out 0ms forwards;
    }

    [data-phase='leaving'] & {
      opacity: 1;
      animation: ${fadeOut} ${MESSAGE_REDUCED_MS}ms ease-out 0ms forwards;
    }
  }
`;
